'use client';

import { Form } from '@/components/forms/Form';
import React, { Dispatch, ReactNode, useMemo, useState } from 'react';
import { RemoveScroll } from 'react-remove-scroll';

import {
	defaultState,
	type ReplaceInPersonFormState,
	StepMap,
	steps,
} from './constants';

import { reventEvent } from './helpers/reventEvent';
import { finalizeHubspotDeal } from './helpers/finalizeHubspotDeal';
import { getContact } from './helpers/getContact';

import { useSplitFormFlow } from '@/hooks/useSplitFormFlow';

import { rootReducer, type ReplaceInPersonFormActionMap } from './reducer';

import { addressGraph } from './steps/AddressStep/routing';
import { contactInfoStepGraph } from './steps/ContactInfoStep/routing';
import { systemIssuesStepGraph } from './steps/SystemIssuesStep/routing';
import { pictureStepGraph } from './steps/PictureStep/routing';
import { systemAgeStepGraph } from './steps/SystemAgeStep/routing';
import { messageStepGraph } from './steps/MessageStep/routing';
import { urgencyStepGraph } from './steps/UrgencyStep/routing';
import { FormContext_Actions } from '@/components/forms/FormContext';
import { FormSubmissionAnimation } from '@/components/FormSubmissionAnimation';
import sendEmail, { UserData } from '@/helpers/sendEmail';
import { buyingDecisionStepGraph } from './steps/BuyingDecisionStep';

export interface ReplaceInPersonFormControllerProps {
	children: ReactNode;
}

const ReplaceInPersonFormController = (
	props: ReplaceInPersonFormControllerProps,
) => {
	const { children } = props;
	const [isSubmitting, setIsSubmitting] = useState(false);

	const onSuccess = () => {
		window.location.href = '/replace/in-person/confirmation/';
	};

	const handleSubmit = async (
		state: ReplaceInPersonFormState,
		dispatch: Dispatch<ReplaceInPersonFormActionMap>,
	) => {
		dispatch({ type: FormContext_Actions.SET_IN_PROGRESS, payload: null });
		setIsSubmitting(true);

		try {
			await getContact(state, dispatch);
		} catch (error) {
			dispatch({
				type: FormContext_Actions.SET_FIELD_ERRORS,
				payload: {
					...state.fieldErrors,
					email:
						(error as Error).message ||
						'An error occurred while creating the contact',
				},
			});
			dispatch({ type: FormContext_Actions.SET_IDLE, payload: null });
			dispatch({
				type: FormContext_Actions.SHOW_VALIDATION_ERRORS,
				payload: true,
			});
			setIsSubmitting(false);
			return;
		}

		finalizeHubspotDeal(state, dispatch).then(async (hubspotResult) => {
			await reventEvent(state, hubspotResult.dealInfo);

			const userData: UserData = {
				referenceID: state.referenceId,
				mtaAnonymousID: state.sessionId,
				firstName: state.firstName,
				lastName: state.lastName,
				email: state.email,
			};

			await sendEmail('in-person-replace', userData);

			const { uniqueUrl, dealId, leadPaymentUrl } =
				hubspotResult.dealInfo ?? {};

			// update deal with unique_url
			await fetch(`/api/hubspot/deal?id=${dealId}`, {
				method: 'PATCH',
				body: JSON.stringify({
					unique_url: uniqueUrl,
					lead_payment_url: leadPaymentUrl,
				}),
			});

			if (hubspotResult.success) {
				onSuccess();
			}
		});
	};

	const defaultFormGraph = useMemo(
		() => ({
			...addressGraph,
			...contactInfoStepGraph,
			...systemIssuesStepGraph,
			...pictureStepGraph,
			...buyingDecisionStepGraph,
			...messageStepGraph,
			...systemAgeStepGraph,
			...urgencyStepGraph,
		}),
		[],
	);

	const { formGraph, isLoading, updatedState } = useSplitFormFlow(
		'InPersonReplaceFlow',
		defaultFormGraph,
		defaultState,
		steps,
	);

	if (!isLoading) {
		return (
			<Form<StepMap, ReplaceInPersonFormState, ReplaceInPersonFormActionMap>
				name="ReplaceInPersonForm"
				onSubmit={handleSubmit}
				defaultState={updatedState}
				reducer={rootReducer}
				graph={formGraph}
				className="flex grow flex-col"
			>
				{isSubmitting && (
					// Important! This conditional should be placed over the children for better z-index management
					<RemoveScroll>
						<FormSubmissionAnimation />
					</RemoveScroll>
				)}
				{children}
			</Form>
		);
	}
};

export default ReplaceInPersonFormController;
